import { Carousel, HeadingTag, Link, ModuleBase, ResponsiveImage, Text } from '@/components';
import tailwindConfig from '@/tailwind.config';
import { isColorDark } from '@/utils';
import classNames from 'classnames';
import { useEffect, useState, useRef } from 'react';
import Flicking from '@egjs/react-flicking';
import '@egjs/react-flicking/dist/flicking.css';
import ComponentAnimation from '@/animations/animationComponent/ComponentAnimation';
import CarouselPagination from '@/components/general/CarouselPagination.jsx';
import { motion } from 'framer-motion';

const TwoPortraitMediaAndRte = ({ data }) => {
  const { headingTitle, description, primaryCTA, secondaryCTA } = data || {};
  const [windowWidth, setWindowWidth] = useState(0);
  const scrollContainerRef = useRef(null);
  const { screens } = tailwindConfig.theme;
  const isDark = isColorDark(data.backgroundColour);
  const flickingRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const willChange = (e) => {
    setCurrentIndex(e.index);
  };

  useEffect(() => {
    const resize = () => {
      setWindowWidth(window.innerWidth);
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const handleWheel = (e) => {
    const flicking = flickingRef.current;
    if (!flicking || flicking.animating) return;

    const current = flicking.index;
    const lastIndex = flicking.panelCount - 1;

    if (e.deltaY > 0) {
      if (current < lastIndex) {
        e.preventDefault();
        flicking.next();
      }
    } else if (e.deltaY < 0) {
      if (current > 0) {
        e.preventDefault();
        flicking.prev();
      }
    }
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    el?.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      el?.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const direction = windowWidth >= parseInt(screens.lg, 10) ? 'y' : 'x';

  const items = data.images.map((image, imageIndex) => (
    <div
      key={imageIndex}
      className="relative aspect-8/10 w-full shrink-0 md:w-[348px] lg:w-[500px] xl:aspect-[700/875] xl:w-[700px] shortDesktop:w-[450px]"
    >
      <ResponsiveImage
        image={image}
        widths={{ xs: 660, sm: 1152, md: 498, lg: 1050, xl: 1050, xxl: 1050 }}
        heights={{ xs: 653, sm: 1536, md: 663, lg: 1313, xl: 1313, xxl: 1313 }}
        className="overlay pointer-events-none"
      />
    </div>
  ));

  const delayText = data.mediaAlignment === 'Left' ? 0.8 : 0.2;
  const delayPicture = data.mediaAlignment === 'Left' ? 0.2 : 0.8;

  const [containerHeight, setContainerHeight] = useState('auto');

  useEffect(() => {
    if (direction === 'y') {
      const count = data.images.length;

      const minHeight = 200; // vh
      const maxHeight = 290; // vh

      const clampedCount = Math.min(Math.max(count, 2), 10);
      const containerVh = minHeight + ((clampedCount - 2) / 8) * (maxHeight - minHeight);

      setContainerHeight(containerVh);
    }
  }, [windowWidth, data.images.length]);

  return (
    <ModuleBase data={data}>
      <motion.div
        ref={scrollContainerRef}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ type: 'tween', duration: 0.8 }}
        className={classNames('relative w-full')}
        style={
          windowWidth && data.images.length > 1 && direction === 'y'
            ? { height: `calc(${containerHeight}vh + 100px)` }
            : undefined
        }
      >
        <div
          className={classNames(
            'main-data container flex flex-col-reverse gap-y-8 pb-0 md:justify-between',
            data.mediaAlignment === 'Left' ? 'md:flex-row-reverse' : 'md:flex-row',
            'lg:sticky lg:top-[15%] shortDesktop:top-[15%]',
          )}
        >
          <ComponentAnimation
            delay={delayText}
            className={classNames('text-wrapper md:my-auto md:w-[348px] xl:w-[496px]')}
          >
            <div className="title-wrapper" role="contentinfo" aria-label="contentinfo">
              {headingTitle && (
                <HeadingTag animate data={headingTitle} className="title-05 color-from-bg font-kepler" />
              )}
            </div>
            {(description || primaryCTA || secondaryCTA) && (
              <div className="mt-4">
                <Text
                  animate
                  dangerouslySetInnerHTML={{ __html: description }}
                  className="color-from-bg body-02 !text-opacity-75"
                />
                {(primaryCTA || secondaryCTA) && (
                  <div className="mt-5 flex flex-col gap-2 md:flex-row lg:mt-6">
                    <Link
                      animate
                      className={classNames('btn primary', isDark ? 'dark' : 'light')}
                      link={data.primaryCTA}
                    />
                    <Link
                      animate
                      className={classNames('btn secondary', isDark ? 'dark' : 'light')}
                      link={data.secondaryCTA}
                    />
                  </div>
                )}
              </div>
            )}
          </ComponentAnimation>

          <ComponentAnimation delay={delayPicture}>
            <div
              className={classNames(
                'relative my-auto overflow-hidden md:h-[435px] md:w-[348px] lg:h-[625px] lg:w-[500px] xl:h-[825px] xl:w-[700px] shortDesktop:h-[562px] shortDesktop:w-[450px]',
              )}
            >
              {data.images.length > 1 ? (
                <>
                  {direction === 'y' && (
                    <>
                      <Flicking
                        ref={flickingRef}
                        horizontal={false}
                        circular={false}
                        onWillChange={willChange}
                        align="center"
                        className="flex h-full flex-col"
                        moveType="snap"
                      >
                        {items}
                      </Flicking>
                      <CarouselPagination
                        current={currentIndex}
                        total={data.images.length}
                        className={`absolute right-6 top-1/2 z-20 hidden -translate-y-1/2 lg:flex`}
                      />
                    </>
                  )}

                  {direction === 'x' && (
                    <Carousel
                      items={items}
                      moduleId={data.moduleId}
                      animationStyle="elegant"
                      showPagination
                      paginationType="slider"
                      className="[&_.carousel-pagination-slider-track]:w-[81px] [&_.carousel-pagination]:absolute [&_.carousel-pagination]:bottom-4 [&_.carousel-pagination]:left-1/2 [&_.carousel-pagination]:-translate-x-1/2"
                    />
                  )}
                </>
              ) : (
                <div className="relative aspect-8/10 w-full md:w-[348px] lg:w-[500px] xl:aspect-[700/875] xl:w-[700px] shortDesktop:w-[450px]">
                  <ResponsiveImage
                    image={data.images[0]}
                    widths={{ xs: 440, sm: 768, md: 332, lg: 700, xl: 700, xxl: 700 }}
                    heights={{ xs: 435, sm: 1024, md: 442, lg: 875, xl: 875, xxl: 875 }}
                    className="overlay pointer-events-none"
                  />
                </div>
              )}
            </div>
          </ComponentAnimation>
        </div>
      </motion.div>
    </ModuleBase>
  );
};

export default TwoPortraitMediaAndRte;
